import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import {PortalSettingsGetSchema} from '../../../../api/DBSchema/PortalSettings';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCButton from '../../../../components/Simple/SMCButton';
import Sepperator from '../../../../components/Simple/Sepperator';
import {useBoolean} from '../../../../utils/hooks';
import Api from '../../../../api/SMPFetchHelper';
import {useSMCErrorHandler} from '../../../../utils/errorUtils';
import EmptyRow from '../../../../components/Simple/EmptyRow';
import ChangePasswordModal from '../../../PopupPages/ChangePasswordModal';
import SMCNumberInput from "../../../../components/Simple/SMCNumberInput";

const pageSchema = PortalSettingsGetSchema.pages.radiusAuth;
const pageId = pageSchema.pageId;

function PortalSettingsRadiusAuth({
    portalSettingsObject,
    setPortalSettingsData
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const pageData = portalSettingsObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setPortalSettingsData([pageId, paramName], value);
    }, [setPortalSettingsData]);

    const radiusPrimaryHostIP   = pageData[pageSchema.radiusPrimaryHostIP];
    const radiusPrimarySecret   = pageData[pageSchema.radiusPrimarySecret];
    const radiusSecondaryHostIP = pageData[pageSchema.radiusSecondaryHostIP];
    const radiusSecondarySecret = pageData[pageSchema.radiusSecondarySecret];
    const radiusPort            = pageData[pageSchema.radiusPort];
    const radiusUserName        = pageData[pageSchema.radiusUserName];
    const radiusUserPassword    = pageData[pageSchema.radiusUserPassword];
    
    const [isChangePasswordWindowOpen, openChangePasswordWindow, closeChangePasswordWindow] = useBoolean(false);
    const [currentPasswordChanged, setCurrentPasswordChanged] = useState();

    const [isFetchingPrimaryConnectionStatus, setIsFetchingPrimaryConnectionStatus] = useState(false);
    const [primaryConnectionTestText, setPrimaryConnectionTestText] = useState('');
    
    const [isFetchingSecondaryConnectionStatus, setIsFetchingSecondaryConnectionStatus] = useState(false);
    const [secondaryConnectionTestText, setSecondaryConnectionTestText] = useState('');

    const testPrimaryConnection = useCallback(() => {
        if (isFetchingPrimaryConnectionStatus) {
            return;
        }

        setIsFetchingPrimaryConnectionStatus(true);
        setPrimaryConnectionTestText('Connecting...');
        Api.testRadiusServer(
            'radiusPrimaryHost',
            radiusPrimaryHostIP,
            radiusPrimarySecret,
            radiusPort,
            radiusUserName,
            radiusUserPassword
        )
        .then(resp => {
            const respData = resp.data;
            if (respData.isRadiusAuthenticated) {
                setPrimaryConnectionTestText(
                    <>
                    <SMCLabel>Authentication accepted</SMCLabel>
                    {respData.radiusUserRole && (
                        <SMCLabel>Radius user role: {respData.radiusUserRole}</SMCLabel>
                    )}
                    </>
                );
            }
            else if (respData.radiusErrorCode) {
                setPrimaryConnectionTestText(t(respData.radiusErrorCode));
            }
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            setPrimaryConnectionTestText(errorMessage);
        })
        .finally(() => {
            setIsFetchingPrimaryConnectionStatus(false);
        });
    }, [getErrorMessage, isFetchingPrimaryConnectionStatus, radiusPrimaryHostIP, radiusPrimarySecret, radiusPort, radiusUserName, radiusUserPassword, t]);

    const testSecondaryConnection = useCallback(() => {
        if (isFetchingSecondaryConnectionStatus) {
            return;
        }

        setIsFetchingSecondaryConnectionStatus(true);
        setSecondaryConnectionTestText('Connecting...');
        Api.testRadiusServer(
            'radiusSecondaryHost',
            radiusSecondaryHostIP,
            radiusSecondarySecret,
            radiusPort,
            radiusUserName,
            radiusUserPassword
        )
        .then(resp => {
            const respData = resp.data;
            if (respData.isRadiusAuthenticated) {
                setSecondaryConnectionTestText(
                    <>
                    <SMCLabel>Authentication accepted</SMCLabel>
                    {respData.radiusUserRole && (
                        <SMCLabel>Radius user role: {respData.radiusUserRole}</SMCLabel>
                    )}
                    </>
                );
            }
            else if (respData.radiusErrorCode) {
                setSecondaryConnectionTestText(t(respData.radiusErrorCode));
            }
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            setSecondaryConnectionTestText(errorMessage);
        })
        .finally(() => {
            setIsFetchingSecondaryConnectionStatus(false);
        });
    }, [getErrorMessage, isFetchingSecondaryConnectionStatus, radiusSecondaryHostIP, radiusSecondarySecret, radiusPort, radiusUserName, radiusUserPassword, t]);

    return (
        <EditorLayout>
            <SMCTextInput
                value={radiusPrimaryHostIP}
                onChange={e => setPageValue(pageSchema.radiusPrimaryHostIP, e.target.value)}
                label="Radius Primary Host IP"
            />
            <RowLayout>
                <SMCLabel useContextWidth>
                    Radius Primary Secret
                </SMCLabel>
                <SMCButton onClick={() => { setCurrentPasswordChanged('primary'); openChangePasswordWindow(); }}>
                    Set
                </SMCButton>
            </RowLayout>
            <SMCTextInput
                value={radiusSecondaryHostIP}
                onChange={e => setPageValue(pageSchema.radiusSecondaryHostIP, e.target.value)}
                label="Radius Secondary Host IP"
            />
            <RowLayout>
                <SMCLabel useContextWidth>
                    Radius Secondary Secret
                </SMCLabel>
                <SMCButton onClick={() => { setCurrentPasswordChanged('secondary'); openChangePasswordWindow(); }}>
                    Set
                </SMCButton>
            </RowLayout>
            <RowLayout>
                <SMCNumberInput
                    value={radiusPort}
                    label={"Radius Port"}
                    onChange={e => setPageValue(pageSchema.radiusPort, parseInt(e.target.value))}
                    width={70}
                />
            </RowLayout>
            <Sepperator />
            <EmptyRow />
            <SMCLabel bold>Radius connectivity test</SMCLabel>
            <SMCTextInput
                value={radiusUserName}
                onChange={e => setPageValue(pageSchema.radiusUserName, e.target.value)}
                label="User Name"
            />
            <RowLayout>
                <SMCLabel useContextWidth>
                    User Password
                </SMCLabel>
                <SMCButton onClick={() => { setCurrentPasswordChanged('user'); openChangePasswordWindow(); }}>
                    Set
                </SMCButton>
            </RowLayout>
            <RowLayout>
                <SMCButton onClick={testPrimaryConnection}>
                    Test primary
                </SMCButton>
                <SMCButton onClick={testSecondaryConnection}>
                    Test secondary
                </SMCButton>
            </RowLayout>
            {
                primaryConnectionTestText && (
                    <SMCLabel><b>Primary: </b>{primaryConnectionTestText}</SMCLabel>
                )
            }
            {
                secondaryConnectionTestText && (
                    <SMCLabel><b>Secondary: </b>{secondaryConnectionTestText}</SMCLabel>
                )
            }
            {/* Popup pages */}
            <ChangePasswordModal
                isOpen={isChangePasswordWindowOpen}
                onRequestClose={closeChangePasswordWindow}
                useMinimumLength={false}
                useMaximumLength={false}
                onFinish={newPassword => {
                    if (currentPasswordChanged === 'primary') {
                        setPageValue(pageSchema.radiusPrimarySecret, newPassword);
                    } else if (currentPasswordChanged === 'secondary') {
                        setPageValue(pageSchema.radiusSecondarySecret, newPassword);
                    } else if (currentPasswordChanged === 'user') {
                        setPageValue(pageSchema.radiusUserPassword, newPassword);
                    }
                    closeChangePasswordWindow();
                }}
            />
        </EditorLayout>
    )
}

export default PortalSettingsRadiusAuth;